/**
 * Every place a pack authors effects — the ONE list (D143, moved here in
 * D233).
 *
 * @remarks
 * Three loader rules read "every effect in the pack" and each had written
 * the sources out by hand — interactions, events, triggers — which held
 * exactly until D143 added a fourth (an equippable item's `onEquip` /
 * `onUnequip` hooks). A rule that enumerates sources by hand is a rule with
 * a blind spot waiting for the next source, so the roster became one module
 * in the loader. D233 moved it HERE, below the loader, because the question
 * "where can an actor ever stand" is answered by every `move_actor` in the
 * pack, and that question is asked by three packages at three tiers — the
 * loader (placement, producibility, `blocksExits`), the prose audit (the
 * referable words per room) and the engine (the static room catalog). One
 * roster, in the lowest package all three share; the loader's
 * `effect-sources.ts` is now a re-export of this.
 *
 * @packageDocumentation
 */

import type { ContentPackDefinition } from './content-pack.js';
import type { EffectDefinition } from './effects.js';
import type { TopicDefinition } from './topics.js';

import { verbInteractionArmsOf } from './verb-interactions.js';

/** One authored effect list and the path it lives at. */
export type EffectHome = readonly [path: string, effects: readonly EffectDefinition[]];

/**
 * D231: one home per topic of a table, at `<owner>.topics[i].effects`.
 *
 * @param topics - The owner's table, or nothing
 * @param ownerPath - The owner's path
 * @returns The homes, in table order
 */
const topicEffectHomesOf = (topics: undefined | readonly TopicDefinition[], ownerPath: string): readonly EffectHome[] =>
	(topics ?? []).map((topic, topicIndex): EffectHome => [`${ownerPath}.topics[${String(topicIndex)}].effects`, topic.effects]);

/**
 * D246: a list's homes — the list itself and, through every `choose` in it
 * at any depth, every arm's effects at `<path>[i].arms[j].effects`.
 *
 * @remarks
 * An arm's effects are an effect home like any other: everything a pack
 * can do inside a branch it can do outside one, so every rule that asks
 * "does anything anywhere do X" must meet it there. Reading the arms as
 * homes, rather than teaching each rule to recurse, is what keeps the rules
 * from each growing their own blind spot — the reason this roster exists.
 * The list comes first, then each branch's arms in arm order, so document
 * order holds.
 *
 * @param home - One effect list and its path
 * @returns It, and every arm's list within it
 */
export const effectHomesWithin = (home: EffectHome): readonly EffectHome[] => {
	const [path, effects] = home;

	return [
		home,
		...effects.flatMap((effect, effectIndex) =>
			effect.type === 'choose'
				? effect.arms.flatMap((arm, armIndex) => effectHomesWithin([`${path}[${String(effectIndex)}].arms[${String(armIndex)}].effects`, arm.effects]))
				: [],
		),
	];
};

/**
 * D246: every effect a list can apply, flat — its own and every arm's, at
 * any depth, a `choose` itself included — for the questions about what an
 * effect list COULD do, where any arm counts.
 *
 * @param effects - One effect list
 * @returns Every effect within it, in document order
 */
export const effectsWithin = (effects: readonly EffectDefinition[]): readonly EffectDefinition[] =>
	effectHomesWithin(['', effects]).flatMap(([, homeEffects]) => homeEffects);

/**
 * Every effect list the pack AUTHORS, with its path — in document order.
 *
 * @param contentPack - The parsed pack
 * @returns The homes, before the branches within them are read
 */
const authoredEffectHomesOf = (contentPack: ContentPackDefinition): readonly EffectHome[] => [
	...contentPack.items.flatMap((item, itemIndex): readonly EffectHome[] => [
		...item.useInteractions.map(
			(interaction, interactionIndex): EffectHome => [
				`items[${String(itemIndex)}].useInteractions[${String(interactionIndex)}].effects`,
				interaction.effects,
			],
		),
		// D196: an authored verb's arms are effect homes like an interaction's.
		...verbInteractionArmsOf(item.verbInteractions).map(
			({ arm, path }): EffectHome => [`items[${String(itemIndex)}].${path}.effects`, arm.effects],
		),
		// D143: the wear hooks are effect homes like any other.
		...(item.equippable === undefined
			? []
			: [
				[`items[${String(itemIndex)}].equippable.onEquip`, item.equippable.onEquip] as EffectHome,
				[`items[${String(itemIndex)}].equippable.onUnequip`, item.equippable.onUnequip] as EffectHome,
			]),
		// D152: what happens to a thing as it wears out — the knife's `remove_item` lives here.
		...(item.durability === undefined ? [] : [[`items[${String(itemIndex)}].durability.onSpent`, item.durability.onSpent] as EffectHome]),
		// D231: a topic's effects fold as an interaction's do, and are homes like one.
		...topicEffectHomesOf(item.topics, `items[${String(itemIndex)}]`),
		// D244: what a stock's draw does once it has been counted.
		...(item.stock?.draws ?? []).map(
			(draw, drawIndex): EffectHome => [`items[${String(itemIndex)}].stock.draws[${String(drawIndex)}].effects`, draw.effects],
		),
	]),
	// D244: what happens the moment a counter reaches its floor or its ceiling.
	...contentPack.counters.flatMap((counter, counterIndex): readonly EffectHome[] => [
		[`counters[${String(counterIndex)}].onMin`, counter.onMin],
		[`counters[${String(counterIndex)}].onMax`, counter.onMax],
	]),
	...contentPack.rooms.flatMap((room, roomIndex): readonly EffectHome[] => topicEffectHomesOf(room.topics, `rooms[${String(roomIndex)}]`)),
	// D232: an actor's table folds effects as a thing's does — a question can change the world.
	// D234: and its fall — `combat.onDefeat` is a home like a wear hook.
	// D235: and every answer the player can give it, and every gift it takes.
	...contentPack.actors.flatMap((actor, actorIndex): readonly EffectHome[] => [
		...topicEffectHomesOf(actor.topics, `actors[${String(actorIndex)}]`),
		...(actor.combat === undefined ? [] : [[`actors[${String(actorIndex)}].combat.onDefeat`, actor.combat.onDefeat] as EffectHome]),
		...(actor.prompts ?? []).flatMap((prompt, promptIndex): readonly EffectHome[] =>
			prompt.options.map(
				(option, optionIndex): EffectHome => [
					`actors[${String(actorIndex)}].prompts[${String(promptIndex)}].options[${String(optionIndex)}].effects`,
					option.effects,
				],
			),
		),
		...actor.giveInteractions.map(
			(interaction, interactionIndex): EffectHome => [`actors[${String(actorIndex)}].giveInteractions[${String(interactionIndex)}].effects`, interaction.effects],
		),
		// D251: and the acts the pack's own verbs do to it; D252: and what it does when it is told.
		...[...verbInteractionArmsOf(actor.verbInteractions), ...verbInteractionArmsOf(actor.orders, 'orders')].map(
			({ arm, path }): EffectHome => [`actors[${String(actorIndex)}].${path}.effects`, arm.effects],
		),
	]),
	...contentPack.events.map((event, eventIndex): EffectHome => [`events[${String(eventIndex)}].effects`, event.effects]),
	...contentPack.triggers.map((trigger, triggerIndex): EffectHome => [`triggers[${String(triggerIndex)}].effects`, trigger.effects]),
];

/**
 * Every effect list in the pack, with its path — in document order, and
 * (D246) every branch's arms among them.
 *
 * @param contentPack - The parsed pack
 * @returns The homes
 */
export const effectHomesOf = (contentPack: ContentPackDefinition): readonly EffectHome[] => authoredEffectHomesOf(contentPack).flatMap(effectHomesWithin);

/**
 * Every effect in the pack, flat — for the rules that ask "does anything
 * anywhere do X". Since D246 that includes every arm of every branch.
 *
 * @param contentPack - The parsed pack
 * @returns Every authored effect
 */
export const everyEffectOf = (contentPack: ContentPackDefinition): readonly EffectDefinition[] =>
	effectHomesOf(contentPack).flatMap(([, effects]) => effects);
